import { TeamsSubscriptionPlans } from "@teams/types";
import React, { useState } from "react";

import { usePaymentRedirect } from "../../actions/usePaymentRedirect";
import { useUserInfo } from "../../actions/useUserInfo";
import { isError } from "../../services/ChannelApi";
import { userApi } from "../../services/UserApi";
import { useTeamsContext } from "../../state/TeamsContextProvider";
import { clientEventTracker } from "../../utils/clientTelemetry";
import { Congratulations } from "./Congratulations";
import { PaymentRedirectPlaceholder } from "./PaymentRedirectPlaceholder";
import { AgilePollyPlanSelection } from "./Plans/AgilePollyPlanSelection";

type Props = {
  onCompleted: () => void;
  trackingSource: string;
};

export const AgileCheckout: React.FC<Props> = ({ onCompleted, trackingSource }) => {
  const [showSuccess, setShowSuccess] = useState<{
    plan: TeamsSubscriptionPlans;
    isTrial: boolean;
  }>();
  const { user, loadUser } = useUserInfo();
  const { teamsContext } = useTeamsContext();
  const [didOpenCheckout, setDidOpenCheckout] = useState(false);
  const { openPaymentPage } = usePaymentRedirect();

  async function requestTrial(plan: TeamsSubscriptionPlans): Promise<string | undefined> {
    const result = await userApi.startTrial(teamsContext, plan);
    if (isError(result) || result.error) {
      if (isError(result) || result.code !== "PREVIOUS-SUBSCRIPTION") {
        clientEventTracker.trackFatalError("trialrequest/error", user, {
          error: result,
          plan,
        });
      }
      return result.message || "Error requesting trial, contact support!";
    }

    loadUser(true);
    setShowSuccess({ plan, isTrial: true });
  }

  async function onPlanSelected(): Promise<void | Error> {
    clientEventTracker.track("UpgradeButton clicked", user, {
      trackingSource,
    });
    openPaymentPage(trackingSource);
    if (!didOpenCheckout) {
      setDidOpenCheckout(true);
    }
  }

  if (showSuccess) {
    return <Congratulations plan={showSuccess.plan} onClose={() => onCompleted()} />;
  }

  return (
    <>
      {!didOpenCheckout ? (
        <AgilePollyPlanSelection
          isTrialMode={true}
          user={user}
          onPlanSelected={onPlanSelected}
          onTrialRequested={plan => requestTrial(plan)}
        />
      ) : (
        <PaymentRedirectPlaceholder
          actionText="Close"
          trackingSource={trackingSource}
          onActionClick={() => {
            loadUser(true);
            setDidOpenCheckout(false);
          }}
        />
      )}
    </>
  );
};
